import { createTheme } from '@mui/material/styles';

// Theme configuration
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#000000',
      light: '#333333',
      dark: '#000000',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#808080',
      light: '#a0a0a0',
      dark: '#606060',
      contrastText: '#ffffff',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#000000',
      secondary: '#666666',
      disabled: '#808080',
    },
    divider: '#e0e0e0',
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#f0f0f0',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    action: {
      hover: '#f0f0f0',
      selected: '#e0e0e0',
    },
  },
  typography: {
    fontFamily: '"Figtree", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    h1: {
      fontSize: '72px',
      fontWeight: 'bolder',
      lineHeight: '5rem',
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '48px',
      fontWeight: 'bold',
      lineHeight: '1.2',
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '38px',
      fontWeight: 'bolder',
      lineHeight: '1.3',
    },
    h4: {
      fontSize: '24px',
      fontWeight: 'bold',
      lineHeight: '1.4',
    },
    h5: {
      fontSize: '20px',
      fontWeight: '600',
      lineHeight: '1.5',
    },
    h6: {
      fontSize: '18px',
      fontWeight: '600',
      lineHeight: '1.5',
    },
    body1: {
      fontSize: '16px',
      lineHeight: '1.75rem',
      fontWeight: '400',
    },
    body2: {
      fontSize: '14px',
      lineHeight: '1.5rem',
      fontWeight: '400',
    },
    subtitle1: {
      fontSize: '16px',
      lineHeight: '1.6',
      fontWeight: '500',
    },
    subtitle2: {
      fontSize: '14px',
      lineHeight: '1.5',
      fontWeight: '500',
    },
    button: {
      fontSize: '16px',
      fontWeight: 'bolder',
      textTransform: 'none',
      lineHeight: '1.5',
    },
    caption: {
      fontSize: '12px',
      lineHeight: '1.4',
    },
  },
  spacing: 8, // Base spacing unit (8px)
  shape: {
    borderRadius: 0, // Default to 0, can be overridden per component
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 'bolder',
          borderRadius: '0px',
          padding: '6px 16px',
          fontSize: '14px',
          minWidth: 'auto',
          '&:hover': {
            backgroundColor: '#000000',
            color: '#ffffff',
          },
        },
        contained: {
          backgroundColor: '#000000',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#333333',
          },
        },
        outlined: {
          borderColor: '#000000',
          color: '#000000',
          '&:hover': {
            borderColor: '#000000',
            backgroundColor: '#f0f0f0',
          },
        },
        text: {
          color: '#000000',
          '&:hover': {
            backgroundColor: '#f0f0f0',
          },
        },
        sizeLarge: {
          padding: '8px 20px',
          fontSize: '14px',
        },
        sizeMedium: {
          padding: '6px 16px',
          fontSize: '14px',
        },
        sizeSmall: {
          padding: '4px 12px',
          fontSize: '12px',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '0px',
            '& fieldset': {
              borderColor: '#e0e0e0',
            },
            '&:hover fieldset': {
              borderColor: '#000000',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#000000',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#000000',
          boxShadow: 'none',
          borderRadius: '0px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '0px',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: '0px',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: '2px',
          border: '0.5px solid #000000',
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          '&:hover': {
            backgroundColor: '#f0f0f0',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '0px',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '16px',
          paddingRight: '16px',
        },
      },
    },
  },
});

// Export theme values for use in non-MUI components
export const themeValues = {
  colors: {
    primary: '#000000',
    secondary: '#808080',
    background: '#ffffff',
    text: {
      primary: '#000000',
      secondary: '#666666',
      disabled: '#808080',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#f0f0f0',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    border: '#e0e0e0',
    hover: '#f0f0f0',
  },
  typography: {
    fontFamily: '"Figtree", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    fontSize: {
      hero: '72px',
      h1: '38px',
      h2: '24px',
      h3: '22px',
      body: '16px',
      small: '14px',
      tiny: '12px',
    },
    fontWeight: {
      bolder: 'bolder',
      bold: 'bold',
      semibold: '600',
      medium: '500',
      regular: '400',
    },
    lineHeight: {
      hero: '5rem',
      h1: '1.3',
      h2: '1.4',
      body: '1.75rem',
      small: '1.5rem',
    },
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },
  borderRadius: {
    none: '0px',
    sm: '2px',
    md: '4px',
    lg: '8px',
    xl: '12px',
    full: '50%',
  },
  shadows: {
    sm: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    md: '0px 4px 8px rgba(0, 0, 0, 0.1)',
    lg: '0px 8px 16px rgba(0, 0, 0, 0.1)',
    hero: '0 0 12px 8px hsla(220, 25%, 80%, 0.2)',
  },
  transitions: {
    default: '0.3s ease',
    fast: '0.15s ease',
    slow: '0.5s ease',
  },
};

export default theme;
